import * as Yup from 'yup';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    TextField,
    Typography,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { OpenModalHookType } from '@hooks/useOpenModal';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { formatUnitCost, getUnitCost, getUnitCostSale, Input } from '@api/Inputs/Input';
import InputCategoriesService from '@api/InputCategories/InputCategoryCtrl';
import { InputCategory } from '@api/InputCategories/InputCategory';
import { Units } from '@src/shared/constants/units';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import Authorization from '@components/Utilities/Authorization';

interface SaveInput extends Input {
    stock?: number;
}
interface ModalSaveInputProps {
    modalSaveInput: OpenModalHookType;
    input?: Input;
    stock?: number;
    onSaveInput: (input: Input, stock?: number) => void;
    isInventory?: boolean;
}

const saveInputSchema = Yup.object({
    uid: Yup.string(),
    name: Yup.string().required('Nombre requerido'),
    inputCategoryId: Yup.string(),
    presentation: Yup.object().shape({
        value: Yup.number().required('Valor requerido'),
        unit: Yup.string().required('Unidad requerida')
    }),
    unit: Yup.object().shape({
        value: Yup.number().required('Valor requerido'),
        unit: Yup.string().required('Unidad requerida')
    }),
    purchaseCost: Yup.number().required('Costo de compra requerido'),
    costOfSale: Yup.number().required('Costo de venta requerido'),
    merma: Yup.number().min(0, 'Mínimo 0%').max(100, 'Máximo 100%'),
    sufficientThreshold: Yup.number().required('Umbral suficiente requerido'),
    lowThreshold: Yup.number().required('Umbral bajo requerido'),
    criticalThreshold: Yup.number().required('Umbral crítico requerido'),
    stock: Yup.number(),
    distributionCentersAvailable: Yup.array().of(Yup.string()).optional()
});

export const ModalSaveInput: React.FunctionComponent<ModalSaveInputProps> = (props) => {
    const { modalSaveInput, input, stock, onSaveInput, isInventory } = props;
    const { distributionCenters } = useSelector((state: RootReducer) => state.krispis);
    const inputCategoriesService = useMemo(() => new InputCategoriesService(), []);
    const [inputCategories, setInputCategories] = useState<InputCategory[]>([]);
    const [isAuthorized, setIsAuthorized] = useState<boolean>(!isInventory);
    const [availableInAll, setAvailableInAll] = useState<boolean>(
        input?.distributionCentersAvailable?.includes('all') ?? true
    );

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors }
    } = useForm<SaveInput>({
        resolver: yupResolver(saveInputSchema)
    });

    const getCategories = async () => {
        showLoader();
        try {
            const data = await inputCategoriesService.getInputCategories();
            setInputCategories(data);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const handleClose = () => {
        modalSaveInput.toggle();
    };

    const onSaveInputForm: SubmitHandler<SaveInput> = async (values) => {
        const { stock, ...inputValues } = values;
        onSaveInput(inputValues, stock);
    };

    useEffect(() => {
        if (modalSaveInput.modalOpen) {
            if (!isInventory) {
                void getCategories();
            }
            reset({
                id: input?.id || undefined,
                name: input?.name || '',
                inputCategoryId: input?.inputCategoryId || '',
                presentation: input?.presentation || {
                    value: 1,
                    unit: ''
                },
                unit: input?.unit || {
                    value: 1,
                    unit: ''
                },
                purchaseCost: input?.purchaseCost || 0,
                costOfSale: input?.costOfSale || 0,
                merma: input?.merma ?? 0,
                sufficientThreshold: input?.sufficientThreshold || 100,
                lowThreshold: input?.lowThreshold || 50,
                criticalThreshold: input?.criticalThreshold || 10,
                stock: stock,
                distributionCentersAvailable: input?.distributionCentersAvailable || ['all']
            });
            setAvailableInAll(input?.distributionCentersAvailable?.includes('all') ?? true);
        }
    }, [modalSaveInput.modalOpen]);

    return (
        <Dialog
            PaperProps={{
                style: { borderRadius: 15, padding: 4 }
            }}
            open={modalSaveInput.modalOpen}
            onClose={handleClose}
            aria-labelledby="modal-save-input-title"
            aria-describedby="modal-save-input-description"
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle id="modal-save-input-title">
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={600}>
                        {watch('id') ? 'Editar insumo' : 'Nuevo insumo'}
                    </Typography>
                    <IconButton color="secondary" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <form id="add-distributionCenter-form" onSubmit={handleSubmit(onSaveInputForm)}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2">
                                Completa los datos para agregar un nuevo insumo a la lista
                            </Typography>
                        </Grid>
                        {!isInventory && (
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="name"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <TextField
                                            fullWidth
                                            required
                                            label="Nombre"
                                            variant="outlined"
                                            value={value}
                                            onChange={onChange}
                                            error={!!errors.name}
                                            helperText={errors.name && errors.name.message}
                                        />
                                    )}
                                />
                            </Grid>
                        )}
                        {!isInventory && (
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="input-category-select-label">Categoria</InputLabel>
                                    <Controller
                                        name="inputCategoryId"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <Select
                                                labelId="input-category-select-label"
                                                id="input-category-select"
                                                value={value}
                                                label="Encargado"
                                                onChange={onChange}
                                                error={!!errors.inputCategoryId}
                                            >
                                                <MenuItem value="">Sin categoria</MenuItem>
                                                {inputCategories.map((category, index) => (
                                                    <MenuItem key={index} value={category.id}>
                                                        {category.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                </FormControl>
                            </Grid>
                        )}
                        <Grid item xs={8} md={4}>
                            <Controller
                                name="presentation.value"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        required
                                        label="Presentacíon"
                                        variant="outlined"
                                        type="number"
                                        value={value}
                                        onChange={onChange}
                                        error={!!errors.presentation?.value}
                                        helperText={errors.presentation?.value && errors.presentation?.value.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={4} md={2}>
                            <FormControl fullWidth>
                                <Controller
                                    name="presentation.unit"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <Select
                                            id="presentation-unit-select"
                                            value={value}
                                            onChange={onChange}
                                            error={!!errors.presentation?.unit}
                                        >
                                            {Units.map((unit, index) => (
                                                <MenuItem key={index} value={unit}>
                                                    {unit}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={8} md={4}>
                            <Controller
                                name="unit.value"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        required
                                        label="Unidad base"
                                        variant="outlined"
                                        type="number"
                                        value={value}
                                        onChange={onChange}
                                        error={!!errors.unit?.value}
                                        helperText={
                                            errors.unit?.value
                                                ? errors.unit?.value.message
                                                : 'Rendimiento de la presentación (las recetas consumirán en esta unidad)'
                                        }
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={4} md={2}>
                            <FormControl fullWidth>
                                <Controller
                                    name="unit.unit"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <Select
                                            id="unit-unit-select"
                                            value={value}
                                            onChange={onChange}
                                            error={!!errors.unit?.unit}
                                        >
                                            {Units.map((unit, index) => (
                                                <MenuItem key={index} value={unit}>
                                                    {unit}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={8} md={6}>
                            <Controller
                                name="purchaseCost"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        required
                                        label="Costo de compra"
                                        variant="outlined"
                                        type="number"
                                        value={value}
                                        onChange={onChange}
                                        error={!!errors.purchaseCost}
                                        helperText={
                                            errors.purchaseCost
                                                ? errors.purchaseCost.message
                                                : 'Referencia para CEDIS: a cuánto se compra la presentación al proveedor'
                                        }
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={8} md={6}>
                            <Controller
                                name="costOfSale"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        required
                                        label="Costo de venta"
                                        variant="outlined"
                                        type="number"
                                        value={value}
                                        onChange={onChange}
                                        error={!!errors.costOfSale}
                                        helperText={
                                            errors.costOfSale
                                                ? errors.costOfSale.message
                                                : 'Precio al que la sucursal compra la presentación al CEDIS en sus pedidos'
                                        }
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Stack bgcolor="#F6F8FA" borderRadius={2} p={1.5} spacing={0.5}>
                                <Typography variant="body2" color="text.secondary">
                                    Costo unitario (compra):{' '}
                                    <b>
                                        {formatUnitCost(
                                            getUnitCost({
                                                purchaseCost: Number(watch('purchaseCost')) || 0,
                                                unit: { value: Number(watch('unit.value')) || 0, unit: '' }
                                            })
                                        )}
                                    </b>{' '}
                                    / {watch('unit.unit') || 'unidad base'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Costo unitario (traspaso a sucursal):{' '}
                                    <b>
                                        {formatUnitCost(
                                            getUnitCostSale({
                                                costOfSale: Number(watch('costOfSale')) || 0,
                                                unit: { value: Number(watch('unit.value')) || 0, unit: '' }
                                            })
                                        )}
                                    </b>{' '}
                                    / {watch('unit.unit') || 'unidad base'}
                                </Typography>
                            </Stack>
                        </Grid>
                        {!isInventory && (
                            <Grid item xs={12} md={4}>
                                <Controller
                                    name="merma"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <TextField
                                            fullWidth
                                            label="Merma esperada (%)"
                                            variant="outlined"
                                            type="number"
                                            value={value}
                                            onChange={onChange}
                                            error={!!errors.merma}
                                            helperText={
                                                errors.merma
                                                    ? errors.merma.message
                                                    : 'Informativa para costeo; no afecta descuentos de stock'
                                            }
                                        />
                                    )}
                                />
                            </Grid>
                        )}
                        {!isInventory && (
                            <>
                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name="sufficientThreshold"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <TextField
                                                fullWidth
                                                required
                                                label="Umbral Suficiente"
                                                variant="outlined"
                                                type="number"
                                                value={value}
                                                onChange={onChange}
                                                error={!!errors.sufficientThreshold}
                                                helperText={
                                                    errors.sufficientThreshold && errors.sufficientThreshold.message
                                                }
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name="lowThreshold"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <TextField
                                                fullWidth
                                                required
                                                label="Umbral Bajo"
                                                variant="outlined"
                                                type="number"
                                                value={value}
                                                onChange={onChange}
                                                error={!!errors.lowThreshold}
                                                helperText={errors.lowThreshold && errors.lowThreshold.message}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name="criticalThreshold"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <TextField
                                                fullWidth
                                                required
                                                label="Umbral Crítico"
                                                variant="outlined"
                                                type="number"
                                                value={value}
                                                onChange={onChange}
                                                error={!!errors.criticalThreshold}
                                                helperText={
                                                    errors.criticalThreshold && errors.criticalThreshold.message
                                                }
                                            />
                                        )}
                                    />
                                </Grid>
                            </>
                        )}
                        {isInventory && stock !== undefined && (
                            <Grid item xs={8} md={6}>
                                <Controller
                                    name="stock"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <TextField
                                            fullWidth
                                            required
                                            label="Stock"
                                            variant="outlined"
                                            type="number"
                                            value={value}
                                            onChange={onChange}
                                            error={!!errors.stock}
                                            helperText={errors.stock && errors.stock.message}
                                        />
                                    )}
                                />
                            </Grid>
                        )}
                        {!isInventory && (
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={availableInAll}
                                            onChange={(e) => {
                                                setAvailableInAll(e.target.checked);
                                                if (e.target.checked) {
                                                    reset({ ...watch(), distributionCentersAvailable: ['all'] });
                                                } else {
                                                    reset({ ...watch(), distributionCentersAvailable: [] });
                                                }
                                            }}
                                        />
                                    }
                                    label="Disponible en todos los CEDIS"
                                />
                            </Grid>
                        )}
                        {!isInventory && !availableInAll && (
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel id="distribution-centers-select-label">CEDIS disponibles</InputLabel>
                                    <Controller
                                        name="distributionCentersAvailable"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <Select
                                                labelId="distribution-centers-select-label"
                                                id="distribution-centers-select"
                                                multiple
                                                value={value || []}
                                                label="CEDIS disponibles"
                                                onChange={onChange}
                                                error={!!errors.distributionCentersAvailable}
                                            >
                                                {distributionCenters.map((center) => (
                                                    <MenuItem key={center.id} value={center.id}>
                                                        {center.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                </FormControl>
                            </Grid>
                        )}
                        {isInventory && (
                            <Grid item xs={12}>
                                <Authorization
                                    onAuthorized={() => setIsAuthorized(true)}
                                    onUnauthorized={() => setIsAuthorized(false)}
                                />
                            </Grid>
                        )}
                    </Grid>
                </form>
            </DialogContent>
            <DialogActions>
                <Box flex={1} textAlign="center">
                    <Button
                        disabled={!isAuthorized}
                        sx={{ width: 300 }}
                        color="primary"
                        variant="contained"
                        type="submit"
                        form="add-distributionCenter-form"
                    >
                        Guardar
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};
